'use strict';
import {
  handleFileDownloadEvent,
  analyticsCtaDataObject,
  analyticsLabelFromString,
  fireAdobeAnalyticsEvent,
} from '../../helpers/adobe-analytics';

const techSpecs = (function() {
  const TECH_SPECS_PATH = '/tech-specs';
  const DOCUMENT_SECTIONS_PATH = '/tech-specs/document-sections';
  const INCREMENT_DOWNLOAD_COUNT_PATH =
    '/tech-specs/increment-document-download-count';

  let SEARCH_TERM = '';

  // The outer container element for all the tech-specs content.
  let techSpecsMainElement;

  function updateBrowserHistory(url) {
    if (typeof history.pushState != 'undefined') {
      const obj = { title: '3form', url: url.href };
      history.pushState(obj, obj.title, obj.url);
    }
  }

  // Fetch rendered documents from the server and update browser
  // history with new url that has the correct query params.
  function reloadDocuments(fetchUrl, browserHistoryUrl = null) {
    const container = document.querySelector('#tech-specs-documents-container');
    container.innerHTML = '<img src="static/images/3form-preloader.gif">';

    const init = { method: 'GET', headers: { Accept: 'text/html' } };
    fetch(fetchUrl.href, init)
      .then(response => {
        if (response.ok) {
          return response.text();
        } else {
          return Promise.reject(response);
        }
      })
      .then(text => {
        container.innerHTML = text;
        if (browserHistoryUrl) {
          updateBrowserHistory(browserHistoryUrl);
        }
        // Since we've reloaded the documents section,
        // we need to re-add event handlers.
        initDocumentsSection(container);
        scrollToResults();

        if (!!SEARCH_TERM) sendAdobeSearchEvent(SEARCH_TERM);
        SEARCH_TERM = '';
      })
      .catch(err => {
        container.innerHTML = '<h3>Error fetching documents</h3>';
        console.warn('Error while fetching document sections: ', err);
      });
  }

  function elementIsFeaturedCategory(element) {
    return element.classList.contains('tech-specs-featured-category');
  }

  function elementIsSubcategory(element) {
    return element.classList.contains('tech-specs-subcategory');
  }

  function updateClass(element, className, add) {
    if (add) {
      element.classList.add(className);
    } else {
      element.classList.remove(className);
    }
  }

  function addParamToHref(href, key, value) {
    const url = new URL(href);
    url.searchParams.set(key, value);
    return url.href;
  }

  function removeParamFromHref(href, key) {
    const url = new URL(href);
    url.searchParams.delete(key);
    return url.href;
  }

  function updateCategoryHref(categoryElement, categoryId, categorySelected) {
    const linkElement = categoryElement.querySelector(
      '.tech-specs-category-link'
    );
    if (categorySelected) {
      linkElement.href = removeParamFromHref(linkElement.href, 'category');
    } else {
      linkElement.href = addParamToHref(
        linkElement.href,
        'category',
        categoryId
      );
    }
  }

  function updateSubcategoryHref(
    subcategoryElement,
    parentCategoryId,
    subcategoryId,
    subcategorySelected
  ) {
    const linkElement = subcategoryElement.querySelector(
      '.tech-specs-category-link'
    );
    if (subcategorySelected) {
      linkElement.href = addParamToHref(
        linkElement.href,
        'category',
        parentCategoryId
      );
    } else {
      linkElement.href = addParamToHref(
        linkElement.href,
        'category',
        subcategoryId
      );
    }
  }

  function getSubcategoryParentId(subcategoriesContainer) {
    return subcategoriesContainer.dataset.parentCategoryId;
  }

  // Add `search` to query params of all category link hrefs.
  function updateCategoryLinksWithSearchTerm(searchTerm) {
    const categories = techSpecsMainElement.querySelectorAll(
      '.tech-specs-category'
    );
    categories.forEach(category => {
      const categoryLink = category.querySelector('.tech-specs-category-link');
      if (searchTerm && searchTerm.length > 0) {
        categoryLink.href = addParamToHref(
          categoryLink.href,
          'search',
          searchTerm
        );
      } else {
        categoryLink.href = removeParamFromHref(categoryLink.href, 'search');
      }
    });
  }

  // Handle all of the required changes to the category elements based on an new category selection.
  function updateCategoryElements(selectedCategoryId) {
    let selectedCategoryElement = null;
    let selectedCategoryIsFeatured = false;
    let selectedCategoryIsSubcategory = false;
    let selectedMainCategoryId = null;

    if (selectedCategoryId) {
      selectedCategoryElement = techSpecsMainElement.querySelector(
        `[data-category-id="${selectedCategoryId}"]`
      );
      selectedCategoryIsFeatured = elementIsFeaturedCategory(
        selectedCategoryElement
      );
      selectedCategoryIsSubcategory = elementIsSubcategory(
        selectedCategoryElement
      );

      if (!selectedCategoryIsFeatured) {
        if (selectedCategoryIsSubcategory) {
          selectedMainCategoryId = getSubcategoryParentId(
            selectedCategoryElement.closest('.tech-specs-subcategories')
          );
        } else {
          selectedMainCategoryId = selectedCategoryId;
        }
      }
    }

    // Update the class and associated link href of every category
    // that's not a subcategory.
    const categoryElements = techSpecsMainElement.querySelectorAll(
      '.tech-specs-category'
    );
    categoryElements.forEach(categoryElement => {
      const categoryId = categoryElement.dataset.categoryId;
      const categorySelected = selectedCategoryId === categoryId;
      if (elementIsFeaturedCategory(categoryElement)) {
        updateClass(
          categoryElement,
          'tech-specs-category-selected',
          categorySelected
        );
        updateCategoryHref(categoryElement, categoryId, categorySelected);
      } else if (!elementIsSubcategory(categoryElement)) {
        const categoryOrSubcategorySelected =
          selectedMainCategoryId === categoryId;
        updateClass(
          categoryElement,
          'tech-specs-category-selected',
          categoryOrSubcategorySelected
        );
        updateClass(
          categoryElement,
          'tech-specs-category-bold',
          selectedMainCategoryId === null
        );
        updateCategoryHref(categoryElement, categoryId, categorySelected);
      }
    });

    // Update the class and associated link href of every subcategory,
    // and set the visibility of each subcategory container based on
    // whether or not the parent category is selected.
    const subcategoryContainers = techSpecsMainElement.querySelectorAll(
      '.tech-specs-subcategories'
    );
    subcategoryContainers.forEach(container => {
      const parentCategoryId = getSubcategoryParentId(container);
      let hideContainer = selectedCategoryId !== parentCategoryId;

      const subcategoryElements = container.querySelectorAll(
        '.tech-specs-subcategory'
      );
      subcategoryElements.forEach(subcategoryElement => {
        const subcategoryId = subcategoryElement.dataset.categoryId;
        const subcategorySelected = selectedCategoryId === subcategoryId;
        if (subcategorySelected) {
          hideContainer = false;
        }
        updateClass(
          subcategoryElement,
          'tech-specs-category-selected',
          subcategorySelected
        );
        updateClass(
          subcategoryElement,
          'tech-specs-category-bold',
          !selectedCategoryIsSubcategory
        );
        updateSubcategoryHref(
          subcategoryElement,
          parentCategoryId,
          subcategoryId,
          subcategorySelected
        );
      });

      updateClass(container, 'tech-specs-hidden', hideContainer);
    });
  }

  // When the selected category changes, we need to update all of the
  // category elements and the hidden category input field for the search form.
  function updatePageForSelectedCategory(selectedCategoryId) {
    updateCategoryElements(selectedCategoryId);
    updateSearchFormHiddenCategoryInput(selectedCategoryId);
  }

  // Update hidden category input in search form with new selected category.
  function updateSearchFormHiddenCategoryInput(selectedCategoryId) {
    const searchForm = techSpecsMainElement.querySelector(
      '#tech-specs-search-form'
    );
    const searchCategoryInput = searchForm.querySelector("[name='category']");
    if (selectedCategoryId != null) {
      searchCategoryInput.disabled = false;
      searchCategoryInput.value = selectedCategoryId;
    } else {
      searchCategoryInput.disabled = true;
      searchCategoryInput.removeAttribute('value');
    }
  }

  // Fetch new documents and update UI to reflect new category being selected.
  function onCategoryLinkClicked(linkElement) {
    sendAdobeCategorySelectEvent(linkElement);
    const linkUrl = new URL(linkElement.href);
    const selectedCategoryId = linkUrl.searchParams.get('category');

    updatePageForSelectedCategory(selectedCategoryId);

    // Reload documents and update browser history.
    const fetchUrl = new URL(linkUrl);
    fetchUrl.pathname = DOCUMENT_SECTIONS_PATH;
    reloadDocuments(fetchUrl, linkUrl);
  }

  // Fetch new documents and update UI based on new search term.
  function onSearchFormSubmitted(searchForm) {
    const searchTerm = searchForm.querySelector("[name='search']").value;
    SEARCH_TERM = searchTerm;
    updateCategoryLinksWithSearchTerm(searchTerm);

    // Build URL from form data, reload documents, and update browser history.
    const formData = new FormData(searchForm);
    const searchParams = new URLSearchParams(formData);
    if (searchTerm.length === 0) {
      searchParams.delete('search');
    }
    const formUrl = new URL(searchForm.action);
    for (const entry of searchParams.entries()) {
      formUrl.searchParams.set(entry[0], entry[1]);
    }
    const fetchUrl = new URL(formUrl);
    fetchUrl.pathname = DOCUMENT_SECTIONS_PATH;
    reloadDocuments(fetchUrl, formUrl);
  }

  // Clears out the current search and selected category and reloads documents.
  function clearSearchTermAndSelectedCategory() {
    updatePageForSelectedCategory(null);
    updateCategoryLinksWithSearchTerm(null);

    // Reload documents and update browser history.
    const historyUrl = new URL(TECH_SPECS_PATH, document.baseURI);
    const fetchUrl = new URL(historyUrl);
    fetchUrl.pathname = DOCUMENT_SECTIONS_PATH;
    reloadDocuments(fetchUrl, historyUrl);
  }

  // Unhide all hidden documents in this document section,
  // and hide "See all" button.
  function onDocumentsSeeAll(seeAllElement) {
    const hiddenDocs = seeAllElement.parentElement.getElementsByClassName(
      'tech-specs-hidden'
    );
    while (hiddenDocs.length > 0) {
      hiddenDocs[0].classList.remove('tech-specs-hidden');
    }
    seeAllElement.style.display = 'none';
  }

  // Update download count for clicked document.
  function onDocumentLinkClicked(documentLink) {
    const documentId = documentLink.dataset.documentId;
    sendAdobeDownloadEvent(documentLink);

    const init = { method: 'GET', headers: { Accept: 'application/json' } };
    fetch(INCREMENT_DOWNLOAD_COUNT_PATH + '?document=' + documentId, init)
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          return Promise.reject(response);
        }
      })
      .then(result => {
        if (!result.success) {
          console.warn(
            'Server unable to update document download count for document id: ',
            documentId
          );
        }
      })
      .catch(err => {
        console.warn('Error while updating document download count: ', err);
      });
  }

  function sendAdobeCategorySelectEvent(categoryLink) {
    const categoryName = categoryLink.dataset.categoryName;
    if (categoryName) {
      const eventData = {
        type: 'click',
        detail: {
          eVar8: `INP|Tech Specs Category|${categoryName}`,
        },
      };
      if (!!getSearchAnalyticsObject()) {
        eventData.detail['search'] = getSearchAnalyticsObject();
      }
      fireAdobeAnalyticsEvent(eventData);
    }
  }

  function sendAdobeSearchEvent(searchTerm) {
    if (searchTerm) {
      const searchObj = getSearchAnalyticsObject();
      const eventData = {
        type: 'click',
        detail: {
          eVar8: 'INP|Tech Specs|Search',
          search: searchObj,
        },
      };
      fireAdobeAnalyticsEvent(eventData);
    }
  }

  function sendAdobeDownloadEvent(documentLink) {
    const row = documentLink.closest('.tech-specs-document');
    if (!row) return;
    const rawData = row.dataset.document;

    try {
      const data = JSON.parse(rawData);
      const downloadData = {
        file_format: data.file_type,
        file_name: analyticsLabelFromString(data.title),
        link_url: data.url,
        link_type: 'DOCUMENT',
        placement: 'Tech Specs',
      };

      if (getSearchAnalyticsObject(row))
        downloadData.search = getSearchAnalyticsObject(row);

      handleFileDownloadEvent(analyticsCtaDataObject(downloadData));
    } catch (e) {
      console.error('Invalid JSON in data-document:', rawData);
    }
  }

  function getSearchAnalyticsObject(rowSelected = null) {
    let searchObj = null;
    const searchValue = new URLSearchParams(window.location.search).get(
      'search'
    );
    if (searchValue) {
      // Determine if results were found
      const container = document.getElementById(
        'tech-specs-documents-container'
      );
      const tables = container.querySelectorAll('table');
      let resultsFound = false;
      tables.forEach(table => {
        if (table.querySelector('tbody tr')) {
          resultsFound = true;
        }
      });

      // Build Search object
      searchObj = {
        term: searchValue,
        results: resultsFound,
        toolName: 'Tech Specs',
      };

      if (rowSelected) {
        const rows = Array.from(
          document.querySelectorAll('.tech-specs-document')
        );
        const position = rows.indexOf(rowSelected) + 1;
        searchObj['position'] = position;
      }
    }
    return searchObj;
  }

  function scrollToResults() {
    if (document.location.href.includes('?category=')) {
      const elmnt = document.getElementById('tech-specs-documents-container');
      elmnt.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Updated page in response to back/forward through browser history.
  function onPopState(href) {
    const url = new URL(href);
    const selectedCategoryId = url.searchParams.get('category');
    const searchTerm = url.searchParams.get('search');
    updatePageForSelectedCategory(selectedCategoryId);
    updateCategoryLinksWithSearchTerm(searchTerm);
    const fetchUrl = new URL(url);
    fetchUrl.pathname = DOCUMENT_SECTIONS_PATH;
    reloadDocuments(fetchUrl);
  }

  // Intercept category link clicks so we can update without browser page reloads.
  function initCategoryLinks() {
    const categoryLinks = techSpecsMainElement.querySelectorAll(
      '.tech-specs-category-link'
    );

    categoryLinks.forEach(linkElement => {
      linkElement.addEventListener('click', event => {
        event.preventDefault();
        onCategoryLinkClicked(linkElement);
      });
    });
  }

  // Only show the "clear search" button when there is something in the search input.
  function updateSearchClearButtonVisibility(searchInput, clearButton) {
    if (searchInput.value.length === 0) {
      clearButton.classList.add('tech-specs-hidden');
    } else {
      clearButton.classList.remove('tech-specs-hidden');
    }
  }

  // Intercept search form submits so we can update without browser page reloads.
  // Also handle the "clear search" button.
  function initSearchForm() {
    const searchForm = techSpecsMainElement.querySelector(
      '#tech-specs-search-form'
    );
    searchForm.onsubmit = event => {
      event.preventDefault();
      onSearchFormSubmitted(searchForm);
    };

    const searchInput = searchForm.querySelector('.tech-specs-search-input');
    const clearButton = searchForm.querySelector('.tech-specs-search-clear');
    clearButton.onclick = event => {
      event.preventDefault();
      // Clear search input and hide clear button.
      searchInput.value = '';
      searchInput.focus();
      updateSearchClearButtonVisibility(searchInput, clearButton);
    };

    searchInput.oninput = () => {
      updateSearchClearButtonVisibility(searchInput, clearButton);
    };

    updateSearchClearButtonVisibility(searchInput, clearButton);
  }

  // Add event handlers to "Clear All" button to clear search term
  // and selected category from results.
  function initClearAllButtons() {
    const searchInput = techSpecsMainElement.querySelector(
      '.tech-specs-search-input'
    );
    const searchClearButton = techSpecsMainElement.querySelector(
      '.tech-specs-search-clear'
    );
    const clearAllButtons = techSpecsMainElement.querySelectorAll(
      '.tech-specs-clear-all-button'
    );
    if (clearAllButtons) {
      clearAllButtons.forEach(
        clearAllButton =>
          (clearAllButton.onclick = event => {
            event.preventDefault();
            clearSearchTermAndSelectedCategory();
            searchInput.value = '';
            updateSearchClearButtonVisibility(searchInput, searchClearButton);
          })
      );
    }
  }

  // Add event handlers to "see all" buttons to unhide documents.
  function initSeeAllButtons() {
    const seeAllButtons = techSpecsMainElement.querySelectorAll(
      '.tech-specs-see-all'
    );
    if (seeAllButtons) {
      seeAllButtons.forEach(
        seeAllButton =>
          (seeAllButton.onclick = () => {
            onDocumentsSeeAll(seeAllButton);
          })
      );
    }
  }

  // When a document is downloaded we need to update its download count.
  function initDocumentLinks() {
    const documentLinks = techSpecsMainElement.querySelectorAll(
      '.tech-specs-document-link'
    );
    if (documentLinks) {
      documentLinks.forEach(link => {
        link.addEventListener('click', () => onDocumentLinkClicked(link));
      });
    }
  }

  // Add event listeners to elements in the documents section.
  function initDocumentsSection() {
    initClearAllButtons();
    initSeeAllButtons();
    initDocumentLinks();
  }

  // Since we're using history.pushState to update the url history,
  // we need to handle popstate events to get the browser back/forward
  // navigation to work properly.
  function addPopStateListener() {
    addEventListener('popstate', event => {
      event.preventDefault();
      onPopState(document.location);
    });
  }

  // Initialize tech specs page, adding all required event handlers.
  function init() {
    initCategoryLinks();
    initSearchForm();
    initDocumentsSection();
    addPopStateListener();
  }

  window.addEventListener('DOMContentLoaded', () => {
    // If we're on the tech specs page, initialize it.
    techSpecsMainElement = document.querySelector('#tech-specs-main');
    if (techSpecsMainElement) {
      init();
    }
  });
})();

export default techSpecs;
